import { useTranslation } from 'react-i18next';
import CustomSelectField from 'components/forms/customSelectField/CustomSelectField';
import { VrboCancellationPolicy } from 'models/Channels';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';

const VRBO_POLICIES_ORDER = [
  VrboCancellationPolicy.RELAXED,
  VrboCancellationPolicy.MODERATE,
  VrboCancellationPolicy.FIRM,
  VrboCancellationPolicy.STRICT,
  VrboCancellationPolicy.NO_REFUND,
  VrboCancellationPolicy.CUSTOM,
];

interface Props {
  name: string;
  label: React.ReactNode;
  placeholder?: string;
  colSmInput?: number;
  colSmLabel?: number;
  labelOnTop?: boolean;
  required?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  onCustomSelected?: () => void;
  showTooltip?: boolean;
  containerClassName?: string;
  leftAddon?: React.ReactNode;
  dataTestId?: string;
}

const VrboCancellationPolicyField = ({
  name,
  label,
  placeholder,
  colSmInput = 7,
  colSmLabel = 5,
  labelOnTop = false,
  required = false,
  isSearchable = true,
  isClearable = false,
  onCustomSelected,
  showTooltip = true,
  containerClassName,
  leftAddon,
  dataTestId,
}: Props) => {
  const { t } = useTranslation();

  const options = VRBO_POLICIES_ORDER.map((value) => ({
    value,
    label: t(
      `pageProperty.mainSettings.vrboSettings.cancellationPolicy.option_${value}`,
    ),
  }));

  const onPolicyChange = (value: string | null) => {
    if (value === VrboCancellationPolicy.CUSTOM) {
      onCustomSelected?.();
    }
  };

  const optionRenderer = ({
    children,
    data: { value },
  }: {
    children: React.ReactNode;
    data: { value: string };
  }) => (
    <div data-testid={`cancellationPolicyOption-${value}`}>
      <div data-testid="label">
        <b>{children}</b>
      </div>
      <div data-testid="description">
        {t(
          `pageProperty.mainSettings.vrboSettings.cancellationPolicy.optionDescription_${
            value as VrboCancellationPolicy
          }`,
        )}
      </div>
    </div>
  );

  return (
    <CustomSelectField
      label={label}
      name={name}
      placeholder={placeholder}
      onChangeHandler={onPolicyChange}
      options={options}
      optionRenderer={optionRenderer}
      colSmInput={colSmInput}
      colSmLabel={colSmLabel}
      labelOnTop={labelOnTop}
      required={required}
      isSearchable={isSearchable}
      isClearable={isClearable}
      containerClassName={containerClassName}
      leftAddon={leftAddon}
      rightTooltip={
        showTooltip ? (
          <TooltipContentWithLearnMore
            labelTooltipText={t(
              'pageProperty.mainSettings.vrboSettings.cancellationPolicyTooltip',
            )}
            learnMoreUrl="https://help.hostfully.com/en/articles/9405853-optimize-and-improve-your-listings-performance-on-vrbo#h_22c00b1bd9"
          />
        ) : undefined
      }
      data-testid={dataTestId}
    />
  );
};

export default VrboCancellationPolicyField;
